import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";

import {
	USER_PREFERENCES_DEFAULTS,
	UserPreferences
} from "../../definitions/api/user-prefs/user-prefs";
import { isLanguage } from "../../definitions/language";
import { isThemeMode } from "../../definitions/theme";
import { AppConfigService } from "../app.config.service";
import { ApiService } from "./api.service";
import { BaseApiService } from "./base-api.service";

/** Date stamp of the v1 announcement bundle. Used only for one-shot migration of the legacy `lastSeenAnnouncementVersion` field. */
const V1_RELEASE_DATE = "2026-05-14";
const V1_TOUR_ID = "v1-language-theme";

interface SettingsResponse {
	id?: string;
	entityId?: string;
	entityType?: string;
	settings?: Record<string, unknown> | null;
}

interface SettingsUpdate {
	settings: Record<string, unknown>;
}

@Injectable({ providedIn: "root" })
export class UserPreferencesService extends BaseApiService {
	constructor(
		private api: ApiService,
		appConfigService: AppConfigService
	) {
		super("mpapi/user-settings", appConfigService.getConfigValue("API_ENDPOINT") as string);
	}

	getPrefs(): Observable<UserPreferences> {
		return this.api
			.get<SettingsResponse | null>(this.url(), "errors.get-user-preferences-failed")
			.pipe(map((response) => this.fromSettings(response)));
	}

	updatePrefs(prefs: UserPreferences): Observable<unknown> {
		const body: SettingsUpdate = { settings: this.toSettings(prefs) };
		return this.api.put(this.url(), body, "errors.update-user-preferences-failed");
	}

	private fromSettings(response: SettingsResponse | null): UserPreferences {
		const raw = (response?.settings ?? {});
		const readBoolean = (
			key: "messagingEmails" | "orderProposalEmails"
				| "fundingEmails" | "orderReportEmails"
		) => {
			const value = raw[key];
			if (typeof value === "boolean") {
				return value;
			}
			if (typeof value === "string") {
				return value.toLowerCase() === "true";
			}
			return USER_PREFERENCES_DEFAULTS[key];
		};
		const themeRaw = raw.theme;
		const languageRaw = raw.language;
		return {
			userId: response?.entityId,
			messagingEmails: readBoolean("messagingEmails"),
			orderProposalEmails: readBoolean("orderProposalEmails"),
			fundingEmails: readBoolean("fundingEmails"),
			orderReportEmails: readBoolean("orderReportEmails"),
			theme: isThemeMode(themeRaw) ? themeRaw : USER_PREFERENCES_DEFAULTS.theme,
			language: isLanguage(languageRaw) ? languageRaw : USER_PREFERENCES_DEFAULTS.language,
			seenTourIds: this.readSeenTourIds(raw),
			toursOptedOut: raw.toursOptedOut === true
		};
	}

	private readSeenTourIds(raw: Record<string, unknown>): string[] {
		const value = raw.seenTourIds;
		if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
			return value;
		}
		const legacy = raw.lastSeenAnnouncementVersion;
		if (typeof legacy === "string" && legacy >= V1_RELEASE_DATE) {
			return [V1_TOUR_ID];
		}
		return [];
	}


	private toSettings(prefs: UserPreferences): Record<string, unknown> {
		const out: Record<string, unknown> = {
			messagingEmails: prefs.messagingEmails,
			orderProposalEmails: prefs.orderProposalEmails,
			fundingEmails: prefs.fundingEmails,
			orderReportEmails: prefs.orderReportEmails
		};
		if (prefs.theme) {
			out.theme = prefs.theme;
		}
		if (prefs.language) {
			out.language = prefs.language;
		}
		if (prefs.seenTourIds && prefs.seenTourIds.length > 0) {
			out.seenTourIds = prefs.seenTourIds;
		}
		if (prefs.toursOptedOut === true) {
			out.toursOptedOut = true;
		}
		return out;
	}
}
